import { useEffect, useRef } from 'react'
import gsap from 'gsap'

export default function HeroSection() {
  const heroRef    = useRef(null)
  const copyRef    = useRef(null)
  const browserRef = useRef(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })
      if (copyRef.current) {
        gsap.set(copyRef.current.children, { opacity: 0, y: 24 })
        tl.to(copyRef.current.children, { opacity: 1, y: 0, duration: 0.7, stagger: 0.1 })
      }
      if (browserRef.current) {
        gsap.set(browserRef.current, { opacity: 0, y: 40 })
        tl.to(browserRef.current, { opacity: 1, y: 0, duration: 0.9, ease: 'power2.out' }, '-=0.4')
      }
    }, heroRef)
    return () => ctx.revert()
  }, [])

  return (
    <section
      ref={heroRef}
      className="relative flex flex-col overflow-hidden"
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse 70% 50% at 50% -5%, rgba(22,163,74,.1) 0%, transparent 65%), #fff',
      }}
    >
      {/* ── Centered copy ── */}
      <div ref={copyRef} className="flex-shrink-0 text-center px-6 pt-28 pb-4 relative z-10">

        {/* Announcement pill */}
        <div className="inline-flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-full px-3 py-1.5 mb-5 shadow-sm">
          <span className="bg-[#166534] text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">New</span>
          <span className="text-xs text-[#64748B] font-medium">Options Win Rate &amp; Profit Factor tracking is live</span>
          <span className="text-xs text-[#94A3B8]">→</span>
        </div>

        {/* Headline */}
        <h1
          className="font-display leading-[1.06] tracking-tight text-[#0A0F1E] mb-3"
          style={{ fontSize: 'clamp(32px, 4.2vw, 56px)', letterSpacing: '-1.5px' }}
        >
          The Smarter Way to<br />
          <span style={{
            background: 'linear-gradient(135deg,#15803D 0%,#16A34A 50%,#4ADE80 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            Build Dividend Income
          </span>
        </h1>

        {/* Subtext */}
        <p className="text-[#64748B] mx-auto mb-5 leading-relaxed" style={{ fontSize: 15, maxWidth: 480 }}>
          Track every dividend, forecast your DRIP snowball, sync your brokerage,
          and manage covered calls — all in one beautifully simple dashboard.
        </p>

        {/* CTA */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <a
            href="/register"
            className="inline-flex items-center gap-2 text-white font-semibold rounded-[10px] transition-all hover:-translate-y-px"
            style={{
              fontSize: 14, padding: '11px 26px',
              background: '#166534',
              boxShadow: '0 2px 4px rgba(22,101,52,.25), 0 8px 24px rgba(22,101,52,.2)',
            }}
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 14 14">
              <path d="M7 1.5v11M3 5.5l4-4 4 4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Start Tracking Free
          </a>
          <a
            href="/login"
            className="inline-flex items-center gap-2 font-medium rounded-[10px] border transition-all hover:border-[#94A3B8] hover:bg-[#F8FAFC]"
            style={{ fontSize: 14, padding: '10px 20px', borderColor: '#E2E8F0', color: '#1E293B' }}
          >
            Sign In
          </a>
        </div>

        {/* Social proof */}
        <div className="flex items-center justify-center gap-3 flex-wrap mb-3" style={{ fontSize: 12, color: '#64748B' }}>
          <div className="flex items-center">
            {[['#166534','KP'],['#1D4ED8','MR'],['#7C3AED','SJ'],['#B45309','AL'],['#0E7490','TW']].map(([bg,init],i) => (
              <div key={init} className="flex items-center justify-center rounded-full text-white font-bold"
                style={{ width:24, height:24, background:bg, border:'2px solid #fff', marginRight:-6, fontSize:8, zIndex:5-i, position:'relative' }}>
                {init}
              </div>
            ))}
          </div>
          <span style={{ marginLeft: 10 }}>Trusted by <strong style={{ color:'#0A0F1E' }}>12,400+</strong> investors</span>
          <span style={{ color:'#E2E8F0' }}>·</span>
          <span><strong style={{ color:'#0A0F1E' }}>$2.4B+</strong> in portfolios tracked</span>
          <span style={{ color:'#E2E8F0' }}>·</span>
          <span>Free forever</span>
        </div>

        {/* App badges */}
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <span className="text-xs text-[#94A3B8]">Also available on:</span>
          <a href="https://apps.apple.com/app/id6761783631" target="_blank" rel="noopener noreferrer">
            <img src="/badge-app-store.svg" alt="Download on the App Store" style={{ height: 36, width: 'auto', display: 'block' }} />
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.safedividendtracker.app" target="_blank" rel="noopener noreferrer">
            <img src="/badge-google-play.png" alt="Get it on Google Play" style={{ height: 52, width: 'auto', display: 'block', marginTop: -8, marginBottom: -8 }} />
          </a>
        </div>
      </div>

      {/* ── Browser stage ── */}
      <div
        ref={browserRef}
        className="relative z-10"
        style={{ margin: '16px 28px 0', maxWidth: 1100, alignSelf: 'center', width: 'calc(100% - 56px)', height: 'calc(100vh - 380px)', minHeight: 320, maxHeight: 520, display: 'flex', flexDirection: 'column' }}
      >
        {/* Floating card: upcoming payment */}
        <div className="absolute z-20 bg-white rounded-xl border border-[#E2E8F0] px-3 py-2.5"
          style={{ top: 28, left: 8, minWidth: 160, boxShadow: '0 4px 20px rgba(0,0,0,.1)', animation: 'bob 4s ease-in-out infinite' }}>
          <div style={{ fontSize: 18, marginBottom: 2 }}>💰</div>
          <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 500 }}>Upcoming Payment</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#166534' }}>$644.30</div>
          <div style={{ fontSize: 10, color: '#94A3B8' }}>JEPQ · pays in 7 days</div>
          <div className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 mt-1.5"
            style={{ background: '#DCFCE7', color: '#15803D', fontSize: 9.5, fontWeight: 600 }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#16a34a' }} /> On Track
          </div>
        </div>

        {/* Floating card: options win rate */}
        <div className="absolute z-20 bg-white rounded-xl border border-[#E2E8F0] px-3 py-2.5"
          style={{ top: 28, right: 8, minWidth: 155, boxShadow: '0 4px 20px rgba(0,0,0,.1)', animation: 'bob 4s ease-in-out infinite', animationDelay: '1s' }}>
          <div style={{ fontSize: 18, marginBottom: 2 }}>🏆</div>
          <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 500 }}>Options · May 2026</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#166534' }}>93% Win Rate</div>
          <div style={{ fontSize: 10, color: '#94A3B8' }}>Profit Factor: 3.67x</div>
        </div>

        {/* Floating card: smart alert */}
        <div className="absolute z-20 bg-white rounded-xl border border-[#E2E8F0] px-3 py-2.5"
          style={{ bottom: '28%', right: 8, minWidth: 172, boxShadow: '0 4px 20px rgba(0,0,0,.1)', animation: 'bob 4s ease-in-out infinite', animationDelay: '0.7s' }}>
          <div className="flex items-start gap-2">
            <div className="flex items-center justify-center rounded-lg flex-shrink-0"
              style={{ width: 28, height: 28, background: '#FEF9C3', fontSize: 13 }}>🔔</div>
            <div>
              <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 500 }}>Smart Alert fired</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#0A0F1E', lineHeight: 1.35 }}>SCHD yield crossed<br/>your 4.5% target</div>
              <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 1 }}>2 minutes ago</div>
            </div>
          </div>
        </div>

        {/* Browser frame */}
        <div className="flex-1 min-h-0 flex flex-col rounded-t-2xl overflow-hidden"
          style={{
            boxShadow: '0 0 0 1px rgba(0,0,0,.07), 0 8px 16px rgba(0,0,0,.06), 0 32px 64px rgba(0,0,0,.12)',
            transform: 'perspective(2400px) rotateX(2deg)',
            transformOrigin: 'top center',
          }}>
          {/* Browser bar */}
          <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 border-b" style={{ background: '#F1F5F9', borderColor: '#E2E8F0' }}>
            <div className="flex gap-1.5">
              <div style={{ width:10, height:10, borderRadius:'50%', background:'#FF5F57' }} />
              <div style={{ width:10, height:10, borderRadius:'50%', background:'#FFBD2E' }} />
              <div style={{ width:10, height:10, borderRadius:'50%', background:'#28C840' }} />
            </div>
            <div className="flex items-center gap-1.5 rounded-md px-2 py-1 flex-1" style={{ background:'#fff', border:'1px solid #E2E8F0', maxWidth:320, fontSize:11, color:'#94A3B8' }}>
              <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                <rect x="1" y="3" width="7" height="5" rx="1" stroke="#94A3B8" strokeWidth="1.2"/>
                <path d="M3 3V2.5a1.5 1.5 0 013 0V3" stroke="#94A3B8" strokeWidth="1.2"/>
              </svg>
              safedividendtracker.com/dashboard
            </div>
          </div>

          {/* Screenshot */}
          <div className="flex-1 min-h-0 overflow-hidden" style={{ background: '#F8FAFC' }}>
            <img
              src="/screenshots/website1.png"
              alt="Safe Dividend Tracker Dashboard"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top left', display: 'block' }}
            />
          </div>
        </div>

        {/* Fade at bottom */}
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ height: 80, background: 'linear-gradient(to top, #fff, transparent)', zIndex: 5 }} />
      </div>


      <style>{`
        @keyframes bob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }
      `}</style>
    </section>
  )
}
